// What a document pane needs to host comments (#13194): the threads, the
// column's open state, the active thread, the composer's draft, and the
// bridge the editor reads (EditorCommentsContext). The pane mounts this once
// and hands the pieces to its header, its editor and its column.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import type { Editor } from "@tiptap/react";
import {
  commentDraftRange,
  commentThreadIdsIn,
  drawsDraft,
  findDraftRange,
  findQuoteRange,
} from "../FpEditor/services/commentMark";
import type {
  CommentDraft,
  CommentSelection,
  EditorCommentsBridge,
} from "../FpEditor/services/documentCommentsBridge";
import { useArtifactComments } from "./artifactHistory";
import { useArtifactHistoryStore } from "./artifactHistoryStore";
import {
  useDocumentComments,
  type DocumentCommentThread,
  type UseDocumentComments,
} from "./documentComments";

const sameIds = (
  a: ReadonlySet<string> | null,
  b: ReadonlySet<string>
): boolean =>
  a !== null && a.size === b.size && [...b].every((id) => a.has(id));

/** The width of the margin card, and the gap it keeps from the text. */
export const MARGIN_CARD_PX = 236;
const MARGIN_GAP_PX = 16;

/** One thread shown beside its text while the column is closed (artboard
 *  1): where, inside the editor's scroll container, the card sits. */
export interface MarginCardPlacement {
  threadId: string;
  container: HTMLElement;
  top: number;
  left: number;
}

/** Where a margin card for this highlight goes, or null when the editor
 *  has no room for one beside its text. */
const marginPlacement = (
  threadId: string,
  highlight: HTMLElement
): MarginCardPlacement | null => {
  const container = highlight.closest<HTMLElement>("#editor-wrapper");
  const prose = container?.querySelector<HTMLElement>(".ProseMirror");
  if (!container || !prose) return null;
  const box = container.getBoundingClientRect();
  const textRight = prose.getBoundingClientRect().right - box.left;
  const room = container.clientWidth - textRight;
  if (room < MARGIN_CARD_PX + 2 * MARGIN_GAP_PX) return null;
  return {
    threadId,
    container,
    top: highlight.getBoundingClientRect().top - box.top + container.scrollTop,
    left: textRight + MARGIN_GAP_PX + container.scrollLeft,
  };
};

export interface DocumentCommentsHost {
  comments: UseDocumentComments;
  /** The column (desktop) or sheet (phone) is open. */
  open: boolean;
  toggle: () => void;
  close: () => void;
  activeThreadId: string | null;
  /** Make a thread active and bring its text into view. */
  activate: (threadId: string) => void;
  draft: CommentDraft | null;
  cancelDraft: () => void;
  /** Post the draft; `sendToAgent` sends it to the document's agent. */
  submitDraft: (body: string, sendToAgent: boolean) => Promise<void>;
  inDocument: (thread: DocumentCommentThread) => boolean;
  bridge: EditorCommentsBridge;
  /** The margin card on screen, if any. */
  margin: MarginCardPlacement | null;
  dismissMargin: () => void;
}

export const useDocumentCommentsHost = ({
  artifactId,
  workspaceId,
}: {
  artifactId: string | null | undefined;
  workspaceId: string;
}): DocumentCommentsHost => {
  const comments = useDocumentComments(artifactId, workspaceId);
  const column = useArtifactComments(artifactId);
  const { show } = column;
  // Page-scoped, beside the column's open state: this pane can re-mount in
  // the other host while comments are open (a viewer-mode switch, or the
  // hand-back when comments opened over version history close), and the
  // active thread and the draft must come with it.
  const activeThreadId = useArtifactHistoryStore((s) => s.activeComment);
  const draft = useArtifactHistoryStore((s) => s.commentDraft);
  const setActiveThreadId = useCallback(
    (threadId: string | null) =>
      useArtifactHistoryStore.setState({ activeComment: threadId }),
    []
  );
  const setDraft = useCallback(
    (selection: CommentDraft | null) =>
      useArtifactHistoryStore.setState({ commentDraft: selection }),
    []
  );
  const [idsInDocument, setIdsInDocument] =
    useState<ReadonlySet<string> | null>(null);
  const editorRef = useRef<Editor | null>(null);
  // A thread whose text should come into view once the document has it
  // (a `?comment=` link opened before the editor loaded).
  const pendingScroll = useRef<string | null>(null);

  const scrollToMark = useCallback((threadId: string): boolean => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed) return false;
    const mark = editor.view.dom.querySelector(
      `[data-comment-thread="${CSS.escape(threadId)}"]`
    );
    if (!mark) return false;
    mark.scrollIntoView({ block: "center" });
    return true;
  }, []);

  const activate = useCallback(
    (threadId: string) => {
      setActiveThreadId(threadId);
      scrollToMark(threadId);
    },
    [scrollToMark, setActiveThreadId]
  );

  const [margin, setMargin] = useState<MarginCardPlacement | null>(null);
  // The thread ids this editor instance has carried since it loaded. A
  // thread among them that the editor now lacks is the reader's own deletion
  // and is never put back; only a thread this editor has never carried (a
  // colleague's, written into the stored body after this editor loaded) is.
  const carried = useRef<Set<string>>(new Set());
  // Bumped per editor instance, so a fresh editor is reconciled with the
  // list it mounts beside.
  const [editorGeneration, setEditorGeneration] = useState(0);
  const onEditor = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
    carried.current = new Set();
    if (editor) setEditorGeneration((n) => n + 1);
    // The margin card lives in the editor's scroll container.
    else setMargin(null);
  }, []);
  const columnOpen = column.open;
  // A highlight click: with the column open, its card becomes active; with
  // it closed, the thread shows as a card in the margin beside its text, or,
  // where the editor has no room for one, the column opens on it.
  const onHighlightClick = useCallback(
    (threadId: string, element: HTMLElement) => {
      setActiveThreadId(threadId);
      if (!columnOpen) {
        const placement = marginPlacement(threadId, element);
        if (placement) {
          setMargin(placement);
          return;
        }
      }
      show();
    },
    [show, columnOpen, setActiveThreadId]
  );
  const dismissMargin = useCallback(() => {
    setMargin(null);
    setActiveThreadId(null);
  }, [setActiveThreadId]);
  // A post in flight: no other comment starts until it has landed or
  // failed, so its composer (and a failure it reports) stays on screen.
  const posting = useRef(false);
  // A comment being written is the one passage drawn in the active shade:
  // starting it ends the active thread.
  const onStartComment = useCallback(
    (selection: CommentSelection) => {
      if (posting.current) return;
      setActiveThreadId(null);
      setDraft({ ...selection, id: uuidv4() });
      show();
    },
    [show, setDraft, setActiveThreadId]
  );
  const onThreadIdsInDocument = useCallback(
    (ids: ReadonlySet<string>) => {
      for (const id of ids) carried.current.add(id);
      setIdsInDocument((previous) => (sameIds(previous, ids) ? previous : ids));
      const pending = pendingScroll.current;
      if (pending && ids.has(pending)) {
        pendingScroll.current = null;
        requestAnimationFrame(() => scrollToMark(pending));
      }
    },
    [scrollToMark]
  );

  // `?comment=<thread id>` (a copied link): the column opens, and once the
  // list has loaded that thread is made active and its text scrolled to; an
  // id that no longer exists leaves nothing active. A link is honoured once
  // per history entry, recorded in the page-scoped store, whichever pane
  // mounts under that entry: a pane mounted again with the link still in
  // the address (a move between side-by-side and the overlay, by version
  // history or by hand), and the Artifacts page opening another document
  // with a raw `pushState` the router never sees, so that document's pane
  // mounts under the same entry, link and all. Neither mount may open the
  // column.
  const { search, key: locationKey } = useLocation();
  const linkedThread = new URLSearchParams(search).get("comment");
  useEffect(() => {
    if (!linkedThread || !artifactId) return;
    const honoured = useArtifactHistoryStore.getState().honouredLink;
    if (honoured?.locationKey === locationKey) return;
    useArtifactHistoryStore.setState({
      honouredLink: {
        locationKey,
        artifactId,
        threadId: linkedThread,
        activated: false,
      },
    });
    show();
  }, [artifactId, linkedThread, locationKey, show]);
  const honouredLink = useArtifactHistoryStore((s) => s.honouredLink);
  const { loaded, threads } = comments;
  useEffect(() => {
    if (!honouredLink || honouredLink.activated) return;
    // Activation belongs to the mount that shows the column: not to another
    // document's pane, and not to a moment when version history holds the
    // column's place (the link waits for the comments column to be open).
    if (honouredLink.artifactId !== artifactId || !loaded || !columnOpen) {
      return;
    }
    useArtifactHistoryStore.setState({
      honouredLink: { ...honouredLink, activated: true },
    });
    const id = honouredLink.threadId;
    if (!threads.some((t) => t.id === id)) return;
    setActiveThreadId(id);
    if (!scrollToMark(id)) pendingScroll.current = id;
  }, [
    honouredLink,
    artifactId,
    loaded,
    columnOpen,
    threads,
    scrollToMark,
    setActiveThreadId,
  ]);

  // Opening the column takes the margin card down (closing it clears the
  // composer and the active thread: closeArtifactHistory).
  useEffect(() => {
    if (column.open) setMargin(null);
  }, [column.open]);

  const { create } = comments;
  const submitDraft = useCallback(
    async (body: string, sendToAgent: boolean) => {
      if (!draft) return;
      const posted = draft;
      posting.current = true;
      let thread: DocumentCommentThread;
      try {
        thread = await create(
          sendToAgent
            ? { quote: posted.quote, body, send_to_agent: true }
            : { quote: posted.quote, body }
        );
      } finally {
        posting.current = false;
      }
      // The thread exists from here. It is anchored by its own draft only,
      // as that draft is when the post returns: an edit during the post may
      // have ended its quote (the store then holds it marked ended). No other
      // comment starts and the composer cannot cancel while a post is out,
      // but a pane remounted mid-post runs its own host, whose guard does not
      // know this post, so the id check stays: a draft started there is not
      // this post's to anchor on or close.
      const current = useArtifactHistoryStore.getState().commentDraft;
      const ownDraft = current !== null && current.id === posted.id;
      try {
        const editor = editorRef.current;
        if (editor && !editor.isDestroyed && ownDraft && !current.ended) {
          // The range the editor draws the quote on (it moves with edits and
          // always holds the quote); an editor not drawing the draft yet
          // (one that has just mounted) is asked where the quote is.
          const range = drawsDraft(editor.state, current)
            ? commentDraftRange(editor.state, current)
            : findDraftRange(editor.state.doc, current);
          // Saved by the editor's autosave, which counts it as a local edit
          // even as the first change after the load.
          if (range) editor.chain().addCommentThread(range, thread.id).run();
        }
      } finally {
        // Only for its own draft: a post that is no longer the comment being
        // written neither closes nor reopens anything.
        if (ownDraft) {
          setDraft(null);
          setActiveThreadId(thread.id);
          show();
        }
      }
    },
    [draft, create, show, setDraft, setActiveThreadId]
  );

  const cancelDraft = useCallback(() => setDraft(null), [setDraft]);

  // The editor ended the quote (an edit changed the quoted text): the draft
  // is marked ended, a new object, so every editor drawing it stops and a
  // remounted one never finds its words elsewhere. The composer stays open.
  // The editor reports the selection it was given; the host matches it by
  // identity and marks its own copy, id and all.
  const onDraftEnded = useCallback((ended: CommentSelection) => {
    const current = useArtifactHistoryStore.getState().commentDraft;
    if (current !== ended) return;
    useArtifactHistoryStore.setState({
      commentDraft: { ...current, ended: true },
    });
  }, []);

  // A colleague's new thread: the server lists it as in the document once
  // the colleague's editor has saved its mark (that save pushes a refetch),
  // and this editor, loaded before, lacks it. The mark is put back on the
  // thread's quote here, with no save of its own, so the reader stays where
  // they are and their next save keeps the colleague's mark. Never a thread
  // this editor has carried and lost: that is the reader's deletion.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed) return;
    const present = commentThreadIdsIn(editor.state.doc);
    for (const thread of threads) {
      if (thread.resolved_at !== null || !thread.in_document) continue;
      if (present.has(thread.id) || carried.current.has(thread.id)) continue;
      const range = findQuoteRange(editor.state.doc, thread.quote);
      if (range) {
        editor.chain().addCommentThread(range, thread.id, "reanchor").run();
      }
    }
  }, [threads, editorGeneration]);

  const openThreadIds = useMemo(
    () => threads.filter((t) => t.resolved_at === null).map((t) => t.id),
    [threads]
  );
  const bridge = useMemo(
    (): EditorCommentsBridge => ({
      openThreadIds,
      activeThreadId,
      draft,
      onEditor,
      onHighlightClick,
      onStartComment,
      onThreadIdsInDocument,
      onDraftEnded,
    }),
    [
      openThreadIds,
      activeThreadId,
      draft,
      onEditor,
      onHighlightClick,
      onStartComment,
      onThreadIdsInDocument,
      onDraftEnded,
    ]
  );

  const inDocument = useCallback(
    (thread: DocumentCommentThread) =>
      idsInDocument ? idsInDocument.has(thread.id) : thread.in_document,
    [idsInDocument]
  );

  return {
    comments,
    open: column.open,
    toggle: column.toggle,
    close: column.close,
    activeThreadId,
    activate,
    draft,
    cancelDraft,
    submitDraft,
    inDocument,
    bridge,
    margin,
    dismissMargin,
  };
};
